import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Autocomplete, Checkbox, colors, Divider, FormControlLabel, FormGroup, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import CryptoJS from 'crypto-js';
import toast from 'react-hot-toast';
import _ from 'lodash';
import DialogConfirmation from '../../components/DialogConfirmation';
import DialogLoading from '../../components/DialogLoading';
import Cookies from 'universal-cookie';
import { fetchWith401Check } from '../../helpers/http';

const API_URL = process.env.REACT_APP_STAGE === 'DEVELOPMENT' ? process.env.REACT_APP_API_HOST : process.env.REACT_APP_API_HOST_PROD;

export default function AddUserDialog({ dialogProps, setDialogProps, fetchUsers }) {
  const cookies = new Cookies();
  const [entries, setEntries] = React.useState({});
  const [organizations, setOrganizations] = React.useState([]);
  const [dialogConfirmationProps, setDialogConfirmationProps] = React.useState({});
  const [loadingProps, setLoadingProps] = React.useState({ show: false, title: 'Loading ...', description: 'Please wait' });

  const handleClickOpen = () => {
    setDialogProps({ ...dialogProps, show: true });
  };

  const handleClose = () => {
    setDialogProps({ ...dialogProps, show: false, metadata: {} });
    setEntries({});
  };

  const handleCheckboxChange = (event) => {
    setEntries({ ...entries, roles: { ...entries.roles, [event.target.name]: event.target.checked } });
  };

  const handleDeleteUser = () => {
    try {
      setLoadingProps({ ...loadingProps, show: true });
      const requestOptions = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
      };

      const id = dialogProps.metadata.id;

      if (!id) return toast.error('ID Not found');

      fetchWith401Check(`${API_URL}/auth/deleteuser?id=${id}`, requestOptions)
        .then(function (response) {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then(function (response) {
          toast.success('User deleted successfully');
          setDialogConfirmationProps({ ...dialogConfirmationProps, show: false, metadata: {} });
          setDialogProps({ ...dialogProps, show: false, metadata: {} });
          fetchUsers();
        })
        .catch(function (err) {
          if (err.status === 409) return toast.error('Username conflict, please use another username.');
          return toast.error(err.message);
        })
        .finally(function () {
          setLoadingProps({ ...loadingProps, show: false });
        });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateUser = (type) => {
    const data = entries;
    if (!data.username) return toast.error('Please add username');
    if (!data.fullname) return toast.error('Please add fullname');
    if (!data.email) return toast.error('Please add email');
    if (!data.roles) return toast.error('Please choose the role');
    if (!data.password && type === 'adduser') return toast.error('Please add password');

    if (data.username.length < 5) return toast.error('Minimal username length is 5 characters');
    if (data.fullname.length < 4) return toast.error('Minimal fullname length is 4 characters');
    if (data.password.length < 12) return toast.error('Minimal password length is 12 characters with a combination of numbers, symbols, and capital letters');

    function validateText(text) {
      const hasNumber = /\d/.test(text);
      const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(text);
      const hasCapital = /[A-Z]/.test(text);

      return hasNumber && hasSymbol && hasCapital;
    }

    function validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }

    if (!validateText(data.password))
      return toast.error('Minimal password length is 12 characters with a combination of numbers, symbols, and capital letters');

    if (!validateEmail(data.email)) return toast.error('Please use a valid email, example yourname@gmail.com');
    if (['verificator', 'editor'].includes(data.roles) && !data.verification_period) return toast.error('Please choose verification period');

    // end of validation
    const secretKey = process.env.REACT_APP_SKEY;
    const encrypted = CryptoJS.AES.encrypt(data.password, secretKey).toString();
    data.password = encrypted;

    let metadata = null;
    if (['verificator', 'editor'].includes(data.roles)) {
      metadata = { verification_period: data.verification_period };
    }

    try {
      setLoadingProps({ ...loadingProps, show: true });
      const requestOptions = {
        method: dialogProps.type === 'adduser' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
        body: JSON.stringify({
          username: data.username,
          name: data.fullname,
          type: data.roles,
          password: data.password,
          metadata,
          email: data.email,
        }),
      };

      const id = dialogProps.metadata.id;

      fetchWith401Check(`${dialogProps.type === 'adduser' ? `${API_URL}/auth/adduser` : `${API_URL}/auth/updateuser?id=${id}`}`, requestOptions)
        .then(function (response) {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then(function (response) {
          toast.success(dialogProps.type === 'adduser' ? 'User created successfully' : 'User updated successfully');
          setDialogProps({ ...dialogProps, show: false });
          setEntries({});
          window.location.reload();
        })
        .catch(function (err) {
          console.log(err);
          if (err.status === 409) return toast.error('Username conflict, please use another username.');
          return toast.error(err.message);
        })
        .finally(function () {
          setLoadingProps({ ...loadingProps, show: false });
        });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchUserInformation = () => {
    if (dialogProps.type === 'edituser') {
      const _abstract = {
        name: '',
        username: '',
        password: '',
        type: '',
        verification_period: '',
      };

      if (!_.isEmpty(dialogProps.metadata)) {
        const _id = dialogProps.id;
        const data = _abstract;

        data.fullname = dialogProps.metadata['name'];
        data.roles = dialogProps.metadata['type'];
        data.username = dialogProps.metadata.username;
        data.verification_period = dialogProps?.metadata['metadata.verification_period'];

        setEntries({ ...entries, ...data });
      }
    }
  };

  React.useEffect(() => {
    fetchUserInformation();
  }, [dialogProps]);

  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'verificator', label: 'Reviewer' },
    { value: 'contributor', label: 'Contributor' },
  ];

  const verification_period = [
    { value: 'alltime', label: 'All time' },
    { value: '2021', label: '2021' },
    { value: '2022', label: '2022' },
    { value: '2023', label: '2023' },
  ];

  return (
    <Dialog open={dialogProps.show} onClose={handleClose} sx={{ padding: 4 }}>
      <DialogTitle>{dialogProps.title}</DialogTitle>
      <DialogContent>
        <Stack direction='column' spacing={1}>
          <Typography>{dialogProps.description}</Typography>
          <Divider />
          <TextField
            margin='dense'
            id='username'
            label='Username'
            type='text'
            fullWidth
            required
            variant='standard'
            value={entries.username}
            onChange={(e) => setEntries({ ...entries, username: e.currentTarget.value.replace(/[\s\W]/g, '').toLowerCase() })}
          />
          <TextField
            margin='dense'
            id='fullname'
            label='Fullname'
            required
            type='text'
            fullWidth
            variant='standard'
            value={entries.fullname}
            onChange={(e) => setEntries({ ...entries, fullname: e.currentTarget.value })}
          />

          <TextField
            margin='dense'
            id='email'
            label='Email address'
            required
            type='email'
            fullWidth
            variant='standard'
            value={entries.email}
            onChange={(e) =>
              setEntries({
                ...entries,
                email: e.currentTarget.value.replace(/\s/g, '').toLowerCase(),
              })
            }
          />

          <TextField
            margin='dense'
            id='password'
            label='Password'
            required
            type='password'
            value={entries.password}
            fullWidth
            variant='standard'
            onChange={(e) => {
              const secretKey = process.env.REACT_APP_SKEY;
              const encrypted = CryptoJS.AES.encrypt(e.currentTarget.value, secretKey).toString();
              const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
              const decrypted = bytes.toString(CryptoJS.enc.Utf8);

              setEntries({ ...entries, password: e.currentTarget.value });
            }}
          />

          <Stack direction='column' pt={2}>
            <Typography variant='body1'>Roles *</Typography>
            <RadioGroup
              aria-labelledby='roles-group'
              value={entries.roles}
              name='roles-type'
              onChange={(e) => setEntries({ ...entries, roles: e.currentTarget.value })}
            >
              {roles.map((item, index) => (
                <FormControlLabel
                  key={index}
                  value={item.value}
                  control={<Radio />}
                  checked={entries.roles === item.value}
                  label={
                    <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='body1'>
                      {item.label}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </Stack>

          {['verificator', 'editor'].includes(entries.roles) && (
            <Stack direction='column' pt={1}>
              <Typography variant='body1'>Verification Period *</Typography>
              <RadioGroup
                aria-labelledby='roles-group'
                value={entries.verification_period}
                name='roles-type'
                onChange={(e) => setEntries({ ...entries, verification_period: e.currentTarget.value })}
              >
                {verification_period.map((item, index) => (
                  <FormControlLabel
                    key={index}
                    value={item.value}
                    control={<Radio />}
                    checked={entries.verification_period === item.value}
                    label={
                      <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='body1'>
                        {item.label}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction='row' spacing={1}>
          <Button sx={{ color: colors.grey[500] }} onClick={handleClose}>
            Cancel
          </Button>
          {dialogProps.type === 'edituser' && (
            <Button
              onClick={() =>
                setDialogConfirmationProps({
                  ...dialogConfirmationProps,
                  show: true,
                  handleClose: () => setDialogConfirmationProps({ ...dialogConfirmationProps, show: false }),
                  title: 'Deletion Confirmation',
                  content: 'Are you sure want to delete this data?',
                  closeButtonText: 'No',
                  agreeButtonText: 'Yes',
                  handleAgree: () => {
                    handleDeleteUser();
                  },
                })
              }
              sx={{ color: colors.red[400] }}
            >
              Delete
            </Button>
          )}

          <Button onClick={() => handleCreateUser(dialogProps.type)}>{dialogProps.type === 'adduser' ? 'Create User' : 'Update User'}</Button>
        </Stack>
      </DialogActions>
      <DialogConfirmation
        open={dialogConfirmationProps.show}
        handleClose={dialogConfirmationProps.handleClose}
        title={dialogConfirmationProps.title}
        content={dialogConfirmationProps.content}
        closeButtonText={dialogConfirmationProps.closeButtonText}
        agreeButtonText={dialogConfirmationProps.agreeButtonText}
        handleAgree={dialogConfirmationProps.handleAgree}
      />
      <DialogLoading props={loadingProps} />
    </Dialog>
  );
}
